import { Link } from '@tanstack/react-router';
import { Bookmark, Menu, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';

import { useWatchlist } from '@/components/domain/use-watchlist';
import { Container, ThemeToggle } from '@/components/ui';
import { useBalance } from '@/hooks/use-balance';
import { useSession } from '@/hooks/use-session';
import { cn } from '@/lib/utils';
import { formatUsdAmount } from '@/utils/format';

import { BrandLogo } from './brand-logo';
import { NotificationsBell } from './notifications-bell';
import { GuestActions, UserMenu } from './user-menu';

/** Гостьові пункти зафіксовані в docs/context/02-home-registration-login.md §1.1. */
const GUEST_NAV = [
	{ to: '/auctions', label: 'Auctions' },
	{ to: '/how-it-works', label: 'How It Works' },
	{ to: '/shipping', label: 'Shipping' },
	{ to: '/pricing', label: 'Pricing' },
	{ to: '/faq', label: 'FAQ' },
	{ to: '/contact', label: 'Contact' },
] as const;

/**
 * Для залогованого вітрина показує робочі розділи (макет Paper «Vehicle
 * Details — A3»): дилер приходить сюди дивитись лоти, і звідси йому треба в
 * свої ставки, а не у FAQ. Маркетингові сторінки лишаються доступними —
 * просто не займають перші місця.
 */
const DEALER_NAV = [
	{ to: '/auctions', label: 'Auctions' },
	{ to: '/bids', label: 'My bids' },
	{ to: '/vehicles', label: 'My vehicles' },
	{ to: '/payments', label: 'Payments' },
	{ to: '/pricing', label: 'Pricing' },
	{ to: '/how-it-works', label: 'How it works' },
] as const;

/** Спека §1: авторизованому клік по логотипу веде на Dashboard. */
function Logo({ authenticated }: { authenticated: boolean }): ReactNode {
	return (
		<Link to={authenticated ? '/dashboard' : '/'} aria-label="Sirius Hub — home">
			<BrandLogo />
		</Link>
	);
}

/**
 * Закладка з лічильником: збережене має бути видно з будь-якої сторінки.
 * На десктопі — лише іконка з бейджем (підпис у aria-label), у мобільному
 * меню — з текстом, бо там іконка без слова губиться серед пунктів.
 */
function WatchlistLink({ onNavigate, labelled = false }: { onNavigate?: () => void; labelled?: boolean }): ReactNode {
	const watchlist = useWatchlist();

	return (
		<Link
			to="/watchlist"
			onClick={onNavigate}
			aria-label={labelled ? undefined : `Watchlist, ${watchlist.count} saved`}
			className={cn(
				'relative inline-flex items-center gap-2 rounded-control text-fg transition-colors hover:bg-surface-muted',
				labelled ? 'px-3 py-2.5 text-sm text-fg-muted' : 'size-10 justify-center',
			)}
			activeProps={{ className: 'bg-surface-muted' }}
		>
			<Bookmark className="size-5" strokeWidth={1.8} />
			{labelled && <span>Watchlist</span>}
			{watchlist.count > 0 && (
				<span
					className={cn(
						'min-w-4.25 rounded-full bg-primary-solid px-1 text-center text-[0.625rem] font-bold leading-3.25 tabular-nums text-primary-fg',
						labelled ? '' : 'absolute right-0.5 top-1 border-2 border-bg',
					)}
				>
					{watchlist.count}
				</span>
			)}
		</Link>
	);
}

/**
 * Доступний баланс — у шапці, а не лише в меню: перед кожною ставкою дилер
 * звіряє, чи вистачить депозиту, і ця цифра має бути перед очима.
 */
function HeaderBalance(): ReactNode {
	const { balance } = useBalance();
	if (!balance) return null;

	return (
		<>
			<span aria-hidden="true" className="mx-1 h-7 w-px bg-border xl:mx-2" />
			<Link
				to="/dashboard"
				className="flex flex-col items-end rounded-control px-2 py-1 leading-4 transition-colors hover:bg-surface-muted"
			>
				<span className="text-[0.6875rem] text-fg-muted">Available</span>
				<span className="font-mono text-sm font-medium tabular-nums text-fg">{formatUsdAmount(balance.availableUsd)}</span>
			</Link>
		</>
	);
}

export function SiteHeader(): ReactNode {
	const [open, setOpen] = useState(false);
	const { user, isPending } = useSession();
	/*
	  Поки сесія невідома — гостьовий набір, а не порожнеча: перші два пункти
	  в обох наборах однакові, тож підміна не смикає рядок так, як смикали б
	  кнопки Log in / Register.
	*/
	const nav = user ? DEALER_NAV : GUEST_NAV;

	return (
		<header className="sticky top-0 z-40 border-b border-border bg-bg/90 backdrop-blur">
			<Container className="flex h-header items-center justify-between gap-4 xl:gap-6">
				<Logo authenticated={user !== null} />

				<nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
					{nav.map((item) => (
						<Link
							key={item.to}
							to={item.to}
							className="whitespace-nowrap rounded-control px-2.5 py-2 text-sm xl:px-3 text-fg-muted transition-colors hover:bg-surface-muted hover:text-fg"
							activeProps={{ className: 'text-fg font-medium bg-surface-muted' }}
						>
							{item.label}
						</Link>
					))}
				</nav>

				<div className="hidden items-center gap-1 lg:flex xl:gap-1.5">
					{/*
					  Від 1280px: на 1024–1279 повний перемикач переповнював шапку
					  («How It Works» ламався на три рядки). Там тема — у футері.
					*/}
					<ThemeToggle className="mr-2 hidden xl:flex" />
					<WatchlistLink />
					{/*
					  Поки сесія невідома, не показуємо нічого: інакше на кожному
					  завантаженні спалахували б Log in / Register, а через мить
					  змінювались на меню користувача.
					*/}
					{isPending ? (
						<div className="h-8 w-40" aria-hidden />
					) : user ? (
						<>
							<NotificationsBell />
							<HeaderBalance />
							<UserMenu user={user} />
						</>
					) : (
						<GuestActions />
					)}
				</div>

				<button
					type="button"
					onClick={() => setOpen((v) => !v)}
					className="grid size-10 place-items-center rounded-md text-fg-muted hover:bg-surface-muted lg:hidden"
					aria-label={open ? 'Close menu' : 'Open menu'}
					aria-expanded={open}
				>
					{open ? <X className="size-5" /> : <Menu className="size-5" />}
				</button>
			</Container>

			<div className={cn('border-t border-border lg:hidden', open ? 'block' : 'hidden')}>
				<Container className="flex flex-col gap-1 py-3">
					{nav.map((item) => (
						<Link
							key={item.to}
							to={item.to}
							onClick={() => setOpen(false)}
							className="rounded-md px-3 py-2.5 text-sm text-fg-muted hover:bg-surface-muted hover:text-fg"
							activeProps={{ className: 'text-fg font-medium bg-surface-muted' }}
						>
							{item.label}
						</Link>
					))}
					<WatchlistLink labelled onNavigate={() => setOpen(false)} />
					<div className="mt-3 flex items-center justify-between gap-3 border-t border-border pt-3">
						<span className="text-sm text-fg-muted">Theme</span>
						<ThemeToggle />
					</div>
					{!isPending && (
						<div className="mt-2 flex gap-2 border-t border-border pt-3 [&>a]:flex-1">
							{user ? (
								<>
									<NotificationsBell />
									<UserMenu user={user} />
								</>
							) : (
								<GuestActions onNavigate={() => setOpen(false)} />
							)}
						</div>
					)}
				</Container>
			</div>
		</header>
	);
}
